import { showMessage } from 'app/store/fuse/messageSlice';
import format from 'date-fns/format';
import _ from '@lodash';
import {
  calculateUnitPriceExact,
  calculateUnitAmountExact,
  calculateLineTotalExact,
  calculateMarginExact,
  getDiscountAmtExact,
  addMoneyExact,
} from './money';
import { styled } from '@mui/material/styles';
import FusePageCarded from '@fuse/core/FusePageCarded';
import FusePageSimple from '@fuse/core/FusePageSimple/FusePageSimple';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import authTypes from 'app/auth/authTypes';
import authRoles from 'app/auth/authRoles';
import * as yup from 'yup';
import pdfViewerWindow from 'app/main/dashboards/main/dialogs/pdfViewerWindow';
import ImageViewerWindow from 'app/main/dashboards/main/dialogs/ImageViewerWindow';
import { resolveFileSrc, contentTypeOf } from './fileSource';
import config from '../../../../config'

export default function dispatchMessage(
  dispatch,
  message,
  type = 'info',
  autoHideDuration = 3000,
  position = 'top'
) {
  dispatch(
    showMessage({
      message,
      variant: type,
      autoHideDuration,
      anchorOrigin: {
        vertical: position,
        horizontal: 'right',
      },
    })
  );
}

export function formatDateString(value, dFormat = 'MMM dd, yyyy') {
  if(_.endsWith(value, "Z")) {
    // Removed Z So new Date() will not convert that given value to local timezone value
    return value ? format(new Date(value.split('Z')[0]), dFormat) : '';
  }
  return value ? format(new Date(value), dFormat) : '';
}

export function selectAndFormatAmount(value, value2,) {
  const amount = value > 0 ? value : value2;
  return `$${amount || '0.00'}`;
}

export function selectAmount(value, value2) {
  const amount = value > 0 ? value : value2;
  return amount || '0.00';
}

// It will return as "0" or "1". Converting like this like since sometime backend excepts "0" or "1"
// If we pass value as "true" first it will change to number value 1 then it will return "1"
export const getNumberBooleanString = (value) => JSON.stringify(Number(value));

// It will return true or false
// From Backend we get "0" or "1". To avoid assignment error we have to change it to boolean
// Number(value) > 0 condition checked since some of the item have value 2
export  const getBooleanValue = (value) => value === 'true' || value === '1' || value === true || Number(value) > 0;

export function calculateWeight(isManage, length, width, height, setValue) {
  if (isManage) {
    const weight = _.round(length * width * height * 0.053 * 2.22);
    if (setValue) {
      setValue('weight', weight)
    } else {
      return weight
    }
  }
  return 0
}

export function checkNonEmptyOrNull(value) {
  return value === "0" || value === 0 || value === null || typeof value === "undefined" || value === "";
}

/**
* This method checks whether given value is null or undefined.
*/
export function checkNullOrUndefined(value) {
  return _.isNull(value) || _.isUndefined(value);
}

/**
 * PGSS-5280: optional free-solo catalog fields (Sub Type) are cleared to null, '', or { name: '' }.
 * Treating the empty object as "present" left product.sub_type_id unchanged on save because the
 * backend only clears when the field is null.
 *
 * Type and Stone Color are required — do not normalize them through this helper expecting a clear.
 */
export function isEmptyCatalogOption(value) {
  if (value === undefined || value === null || value === '') {
    return true;
  }
  if (typeof value === 'object') {
    if (value.id != null && value.id !== '') {
      return false;
    }
    const label = value.inputValue || value.name;
    return !label;
  }
  return false;
}

/** Persist cleared catalog options as null, never as { name: '' }. */
export function normalizeCatalogOption(value) {
  return isEmptyCatalogOption(value) ? null : value;
}

/** Line-level string form of a catalog option (or null when cleared). */
export function catalogOptionLabel(value) {
  if (isEmptyCatalogOption(value)) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.inputValue || value.name || null;
}

/**
* This method returns true if there is any keys in it.
*/
export function isValidObject(value) {
  if ((value && typeof value === "object" && Object.keys(value).length === 0) ||  _.isNull(value) || _.isUndefined(value)) {
    return false;
  }
  return true;
}

/**
* This method check given value is valid number
*/
export const isValidNumber = (value) => {
  return value !== null && value !== undefined && value !== "" && !Number.isNaN(parseFloat(value))  && parseFloat(value) > 0;
};

/**
* This method removes null and unundefined values from object.
* Works for nested object too
*/
export const cleanData = (inputObject) => {
  if (Array.isArray(inputObject)) {
    return inputObject
      .map(cleanData)
      .filter((item) => item !== null && item !== undefined  && item !== '' && !(typeof item === 'object' && Object.keys(item).length === 0));
  }

  if (typeof inputObject === "object" && inputObject !== null) {
    const cleanedObject = Object.fromEntries(
      Object.entries(inputObject)
        .map(([key, value]) => [key, cleanData(value)])
        .filter(([key, value]) => value !== null && value !== undefined && value !== '' && !(typeof value === 'object' && Object.keys(value).length === 0))
    );

    // If the cleaned object is empty, return undefined to skip it
    return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
  }
  return inputObject;
};


export function selectAndFormatWeight(val1, val2) {
  const weight = val1 > 0 ? val1 : val2;
  return `${numberFormatter.format(weight) || '0'} `
}

export function formatWeight(weight, showEmpty = true) {
  if(weight && weight !== null) return `${numberFormatter.format(weight)} `
  return showEmpty ?  '' : '0 ';
}

export function calculateAndFormatTotalWeight(quantity, weight, showEmpty = true) {
  if(!checkNullOrUndefined(quantity) && !checkNullOrUndefined(weight))
    return `${numberFormatter.format(quantity * weight)}`
  return showEmpty ?  '' : '0 ';
}


export function emptySchema() {
  return yup.object().shape({});
}

export function isCustomer(role) {
  return role === 'customer';
}

export function isVasDesignerRole(role) {
  return  role === 'vas designer';
}

export function isVasManagerRole(role) {
  return  role === 'vas manager';
}

export function isVasProdutionRole(role) {
  return  role === 'vas production';
}

export function isVasTeam(role) {
  return role === 'vas designer' || role === 'vas production' || role === 'vas manager';
}

export function isSupplier(role) {
  return role === 'supplier';
}

/** Label for order header customer field — supplier sees code only (matches list columns). */
export function getOrderCustomerLabel(role) {
  return isSupplier(role) ? 'Customer Code' : 'Customer Name';
}

/**
 * Display value for order header customer field.
 * Supplier: customer_code / company_code only (SupplierView API — no customer_name).
 * Staff/customer: company name fields.
 */
export function getOrderCustomerDisplay(role, orderData, { isCommunicationTab = false } = {}) {
  if (!orderData) {
    return '-';
  }
  if (isCommunicationTab) {
    return orderData?.customer?.company_name || '-';
  }
  if (isSupplier(role)) {
    return orderData.customer_code || orderData.company_code || '-';
  }
  return (
    orderData.customer_name ||
    orderData.company_name ||
    orderData?.customer?.company_name ||
    '-'
  );
}

/**
 * Customer-portal dashboard widget section title (RecentOpenOrders).
 * Custom quote/rfq labels apply only when role is customer; staff/supplier unchanged.
 */
export function getRecentOpenOrdersSectionTitle({ role, isManage, stage, type, headerTitle }) {
  const prefix = isManage ? 'Open ' : 'Recent ';
  if (type) {
    return `${prefix}${type}`;
  }
  if (isCustomer(role)) {
    if (stage === 'rfq') {
      return `${prefix}Quotes Requested for review`;
    }
    if (stage === 'quote') {
      return `${prefix}Quotes Waiting For Approval`;
    }
  }
  return `${prefix}${headerTitle}`;
}

export function isAnyCustomerRole(role) {
  return authRoles.customer.includes(role)
}

export function isAnyVASRole(role) {
  return authRoles?.vasUsers?.includes(role)
}

export function isSales(role) {
  return role === 'sales';
}


export function isStaffWithoutPriceView(roleView) {
  return roleView === 'StaffWithoutPriceView';
}

/**
 * True when ANY of the user's roles is a without-price role. `user.role_view` holds only one role's view (the
 * backend keeps the last role's, or admin's), so a user who also holds another role can miss it there; every
 * role in `user.roles` carries its own `role_view`. Admin wins, as everywhere else: an admin who also holds a
 * without-price role (VAS Manager, VAS Designer, Warehouse ...) still sees prices.
 */
export function hasStaffWithoutPriceRole(user) {
  if (!user) return false;
  if (isStaffWithoutPriceView(user.role_view)) return true;
  const roles = Array.isArray(user.roles) ? user.roles : [];
  const roleName = (r) => String(typeof r === 'string' ? r : r?.name || '').toLowerCase();
  if (String(user.role || '').toLowerCase() === 'admin' || roles.some((r) => roleName(r) === 'admin')) return false;
  return roles.some((r) => isStaffWithoutPriceView(r?.role_view));
}

export function isSuppllierArtDesign(user) {
  if (!isSupplier(user.role)) return false
  const supplierTypes = user?.supplier?.supplier_types || []
  return authTypes.onlyArtDesign.some(str1 => supplierTypes.includes(str1))
}

export function isSupplierStone(user) {
  if (!isSupplier(user.role)) return false
  const supplierTypes = user?.supplier?.supplier_types || []
  return authTypes.onlyStone.some(str1 => supplierTypes.includes(str1))
}

export function isSupplierEtching(user) {
  if (!isSupplier(user.role)) return false
  const supplierTypes = user?.supplier?.supplier_types || []
  return authTypes.onlyEtching.some(str1 => supplierTypes.includes(str1))
}

export function isSupplierSandblasting(user) {
  if (!isSupplier(user.role)) return false
  const supplierTypes = user?.supplier?.supplier_types || []
  return authTypes.onlyEtching.some(str1 => supplierTypes.includes(str1))
}

export function isDefaultSupplierTypes(user) {
  if (!isSupplier(user.role)) return false
  const supplierTypes = user?.supplier?.supplier_types || []
  return authTypes.defaultSupplierTypes.some(str1 => supplierTypes.includes(str1))
}

export function maxNumeric(value, maxLength, specialCharacter, field) {
  value = value.replace(new RegExp(`[^0-9${specialCharacter}]`, 'g'), '');
  if (value.length <= maxLength) {
    field.onChange(value)
  }
}

export const AuraPageCardedRoot = styled(FusePageCarded)({
  '& .FusePageCarded-header': {},
  '& .FusePageCarded-toolbar': {},
  '& .FusePageCarded-content': {},
  '& .FusePageCarded-sidebarHeader': {},
  '& .FusePageCarded-sidebarContent': {},
});

export function formatCustomer(customer, includeShipTo = null) {
  // If shipTo exists and includeShipTo is true, return company_name + shipTo
  if (includeShipTo && customer &&_.has(customer, 'shipTo')) {
    // Build a display string for a customer by combining company_name and shipTo.
    // Only include values that exist, and join them with " - ".
    return [customer?.company_name, customer?.shipTo].filter(Boolean).join(' - ');
  }
  // Otherwise, use the original formatting
  let displayName = customer.company_name || '';
  displayName += customer.first_name || customer.last_name ? '-' : '';
  displayName += `${customer.first_name} ` || '';
  displayName += customer.last_name || '';
  return displayName;
}

export function formatName(firstName, lastName) {
  return `${firstName || ''} ${lastName || ''}`;
}

export function getOrderTotalWeight(orders = []) {
  return _.sumBy(getOrderSummary(orders), (order) => {
    return order?.totalWeight;
  });
}
export function getContainerOrderTotalWeight(orders = []) {
  return _.sumBy(getContainerOrderSummary(orders), (order) => {
    return order?.totalWeight;
  });
}

export function getFilteredSplitOrders(orders = [], splitOrders = []) {
  const orderDetails = []
  // PGSS-5763: a line not ticked on an order that is shipping moves to the split order whole.
  const unticked = new Map()
  if (orders?.length) {
    orders.forEach((o) => {
      const lines = o?.order_detail || []
      if (!lines.some(od => od.is_selected)) return
      lines.forEach(od => (od.is_selected ? orderDetails.push(od) : unticked.set(od?.id, od)))
    })
  }
  return _.reduce(splitOrders, (rows, item) => {
    const line = unticked.get(item?.id)
    if (line) {
      const quantity = Number(line.quantity || 0)
      if (quantity > 0) {
        rows.push({ ...item, quantity, amount: (quantity * Number(item?.unit_price ?? line.unit_price ?? 0)).toFixed(2) })
      }
    } else if (orderDetails.find(od => od?.id === item?.id) && item?.quantity > 0) {
      rows.push(item)
    }
    return rows
  }, []);
}

export function getOrderSummary(orders, customer) {
  return _.map(orders, (order) => {
    const totalWeight = _.sumBy(order.order_detail, (detail) => {
      const qty = detail?.newQuantity ?? detail.selected_quantity
      return detail.is_selected ? _.multiply(qty, detail.weight) : 0
    });
    const totalQuantity = _.sumBy(order.order_detail, (detail) => {
      const qty = detail?.newQuantity ?? detail.selected_quantity
      return qty;
    });
    const selected = _.some(order.order_detail, 'is_selected');
    // const selected = order.order_detail?.filter(detail => detail.is_selected === 1)
    return {
      id: order.id,
      totalWeight,
      totalQuantity,
      selected,
      customerId: customer?.id,
      customerName: customer?.name,
    };
  });
}

export function getContainerOrderSummary(orders, customer) {
  return _.map(orders, (order) => {
    const totalWeight = _.sumBy(order.order_detail, (detail) => {
      return detail.is_selected ? _.multiply(_.toNumber(detail?.quantity || 0), _.toNumber(detail.weight)) : 0
    });
    const orderWeight = _.sumBy(order.order_detail, (detail) => {
      return _.multiply(_.toNumber(detail?.quantity || 0), _.toNumber(detail.weight))
    });
    const totalQuantity = _.sumBy(order.order_detail, (detail) => {
      return _.toNumber(detail?.quantity || 0);
    });
    const totalSelectedQuantity = _.sumBy(order.order_detail, (detail) => {
      return detail.is_selected ? _.toNumber(detail?.quantity || 0) : 0;
    });
    const selected = _.some(order.order_detail, 'is_selected');
    // const selected = order.order_detail?.filter(detail => detail.is_selected === 1)
    return {
      id: order.id,
      totalWeight,
      orderWeight,
      displayWeight: totalWeight > 0 ? totalWeight : orderWeight,
      totalQuantity,
      totalSelectedQuantity,
      selected,
      customerId: customer?.id,
      customerName: customer?.name,
    };
  });
}

export const AuraSimplePageRoot = styled(FusePageSimple)(({ theme }) => ({
  '& .FusePageSimple-header': {
    backgroundColor: theme.palette.background.paper,
    borderBottomWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
  },
  '& .FusePageSimple-toolbar': {},
  '& .FusePageSimple-content': {},
  '& .FusePageSimple-sidebarHeader': {},
  '& .FusePageSimple-sidebarContent': {},
}));

export const AuraSimpleTabPageRoot = styled(FusePageSimple)(({ theme }) => ({
  '& .FusePageSimple-header': {
    backgroundColor: theme.palette.background.paper,
    borderBottomWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    '& .aura-header': {
      padding: '8px 16px',
    }
  },
  '& .FusePageSimple-toolbar': {},
  '& .FusePageSimple-content': {
    flexDirection: 'column',
  },
  '& .FusePageSimple-sidebarHeader': {},
  '& .FusePageSimple-sidebarContent': {},
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const mapParams = (keys, removeKeys = []) => {
  const mapperStrings = {
    'App-Pagination-Num': 'pageNo',
    'App-Pagination-Limit': 'limit',
    'App-Pagination-Sort': 'sort'
  }
  const searchParams = new URLSearchParams(window.location.search);
  const keyValues = Object.fromEntries(searchParams);
  const obj = { ...keyValues }
  Object.keys(keys).forEach((key) => {
    const value = mapperStrings[key];
    if (value)
      obj[value] = keys[key];
    else
      obj[key] = keys[key]
  });

  return _.omit(obj, removeKeys)
}

export const getParams = (goToFirstPage) => {
  const mapperStrings = {
    'pageNo': 'App-Pagination-Num',
    'limit': 'App-Pagination-Limit',
    'sort': 'App-Pagination-Sort'
  };
  const { history, location } = window
  const sp = new URLSearchParams(window.location.search);
  // Go to first page if actions done in some other page
  // This will avoid showing empty results
  if (goToFirstPage && sp.has('pageNo')) {
    sp.delete('pageNo')
    history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }
  const keys = Object.fromEntries(sp);
  const headerObj = {};
  const filterObj = {};
  Object.keys(keys).forEach((key) => {
    const value = mapperStrings[key];
    if (value)
      headerObj[value] = keys[key];
    else
      filterObj[key] = keys[key]
  });

  return { headerObj, filterObj };
}

export const isValSelected = (val) => {
  return val === 1 || val === true || val === "1" || val === "true";
}

export const getSelectedRecords = (items) => {
  return _.filter(
    items || [],
    function (item) {
      return (
        item.is_selected === 1 || item.is_selected === true || item.is_selected === "1" || item.is_selected === "true"
      );
    }
  );
}

export const getValidSelectedRecords = (items) => {
  return _.filter(
    items || [],
    function (item) {
      return (
        (item.is_selected === 1 || item.is_selected === true || item.is_selected === "1" || item.is_selected === "true") && item.original_qty > 0
      );
    }
  );
}

export const isAdminOrStaff = (user) => {
  return user && (user.role === 'admin' || user.role === 'staff' || user.role === 'sales');
}

export const convertArrayToString = (arrayP) => {
  if (!Array.isArray(arrayP) || arrayP?.length === 0) return ''; // Handle invalid input gracefully
  return arrayP.map((obj) => obj.trim()).join(',');
};

// Order Item: Single unit price without discount
/*
 * PGSS-5025 — money maths lives in ./money.js. Do NOT reintroduce float arithmetic here: a JS Number is a
 * binary double and cannot represent decimal cents, so `float + .toFixed(2)` disagrees with the backend's
 * BigDecimal HALF_UP by 1¢. See money.parity.test.js / money.contract.test.js (and PriceCalculationParityTest
 * on the backend) — they will fail if these drift.
 */

/**
 * PGSS-4832 / PGSS-5123 — the single source for "what is this product's FOB cost?".
 *
 * Mirrors the backend's canonical rule (OrderService.calculateUnitPrice / ShipmentService:
 * "Product cost priority: FOB > UnitCost > ZERO"): prefer `fob` only when it is > 0, otherwise
 * `unit_cost`. A plain `fob ?? unit_cost` is WRONG — custom products store `fob = 0` (a real zero,
 * not null) with the true cost in `unit_cost`, so `??` would keep 0 and zero out their price.
 *
 * Exists because this rule was previously copy-pasted per call site and a display that missed it showed
 * an inventory product's `unit_cost` in the FOB column (PGSS-5123). Use this everywhere FOB is read or
 * pre-filled; do not re-inline the ternary.
 *
 * @param {{fob?: any, unit_cost?: any}} source product / order line carrying fob + unit_cost
 * @returns the effective FOB cost (may be undefined/null if neither is set — callers format as needed)
 */
/**
 * The product's cost basis. Prefer effective_fob — the backend (ProductCostResolver) has already applied the
 * rule, including the custom-product case where the higher of fob / unit_cost wins. The frontend should not
 * re-derive it; four copies of this rule is what caused PGSS-4832 -> PGSS-5006 -> PGSS-5123 in the first place.
 *
 * The inline fallback stays only for payloads that predate effective_fob (it is on ProductResponse, not on
 * every endpoint the order screens read) and is deliberately NOT custom-aware — anything needing the custom
 * rule must get it from the backend.
 */
export const resolveFobCost = (source) => {
  if (!checkNullOrUndefined(source?.effective_fob)) {
    return source.effective_fob;
  }
  return Number(source?.fob) > 0 ? source?.fob : source?.unit_cost;
};

/**
 * PGSS-5377 — what the FOB field should SHOW, which is not always what the line COSTS.
 *
 * `resolveFobCost` falls back to `unit_cost` whenever `fob` is not > 0, so a FOB the user
 * deliberately cleared on the Order page resolves straight back to the old cost and looks as
 * though the edit was ignored. `unit_cost` still holds that old value on purpose — supplier
 * lines are priced from it (PGSS-5006) and the dialog must not overwrite it.
 *
 * An explicit zero is therefore the only way "cleared" is representable, and this reads it:
 * a non-null `fob` of 0 means the user cleared the field and must display as cleared. A null
 * `fob` still means "never set", and keeps the existing fallback.
 *
 * DISPLAY ONLY. Do not use this where a line's cost basis is seeded (Order.js, ProductsTab.js
 * both assign resolveFobCost into `unit_cost` on the line) — PGSS-5377 asks for the stale value
 * to stop being displayed, not for the cost basis to change.
 */
export const resolveFobDisplay = (source) => {
  const raw = source?.fob;
  if (!checkNullOrUndefined(raw) && raw !== '' && Number(raw) === 0) {
    return 0;
  }
  return resolveFobCost(source);
};

/**
 * PGSS-5574 — supplier line price source (Unit Cost only). Never use resolveFobCost here.
 *
 * Returns the numeric unit_cost when it is > 0. Returns null when Unit Cost is missing/≤0 while
 * FOB is set — that is the Blue Pearl / order 27447 trap (staff shows FOB, supplier would get $0).
 * Callers must block Create Supplier Order / Reorder in that case rather than posting $0.
 */
export const resolveSupplierUnitCost = (source) => {
  const unitCost = Number(source?.unit_cost);
  if (!Number.isNaN(unitCost) && unitCost > 0) {
    return unitCost;
  }
  const fob = Number(source?.fob);
  if (!Number.isNaN(fob) && fob > 0) {
    return null;
  }
  return Number.isNaN(unitCost) ? 0 : unitCost;
};

/** Products that would create a $0 supplier line while staff FOB is set (PGSS-5574). */
export const findMissingSupplierUnitCosts = (products = []) =>
  (products || []).filter((product) => resolveSupplierUnitCost(product) === null);

/**
 * PGSS-5138 — which margin an order/quote line should carry when it is re-priced against a customer.
 *
 * Mirrors the backend rule (PricingDefaultsResolver.resolveLineMargin): the resolved customer margin
 * wins whenever one is available; otherwise the line keeps the margin it already has. A customer margin
 * of a genuine `0` still wins — only `null`/`undefined`/`''` (no customer margin resolved) falls through
 * to the line's own value. Converting an RFQ whose customer was changed must re-apply this so the quote
 * shows the latest customer's margin, not the one baked in under the previously selected customer.
 */
export const resolveLineMargin = (customerMargin, lineMargin) => {
  return (customerMargin !== undefined && customerMargin !== null && customerMargin !== '')
    ? customerMargin
    : lineMargin;
};

export const calculateUnitPrice = (weight, fob, tariff, shipping, margin = 0) => {
  const priceVal = calculateUnitPriceExact(weight, fob, tariff, shipping, margin);
  if (Number(priceVal)) return priceVal;
  // Zero after round: legacy returned string "0.00" when the pre-round float was truthy (tiny),
  // and number 0 when it was 0/NaN. Reconstruct that gate so e.g. fob=0.001 → "0.00" not 0.
  const w = Number(weight);
  const f = Number(fob);
  const t = Number(tariff);
  const s = Number(shipping);
  const m = Number(margin ?? 0);
  if (![w, f, t, s, m].every(Number.isFinite) || m >= 100) return 0;
  const denom = 1 - m / 100;
  if (!denom) return 0;
  const v = (f + w * s + f * (t / 100)) / denom;
  return v && Number(v) ? priceVal : 0;
}

// Order Item: Single unit price amount with discount
export const calculateUnitAmount = (unitPrice, discount) => {
  const amount = calculateUnitAmountExact(unitPrice, discount > 0 ? discount : 0);
  // Legacy return-type contract (pre-PGSS-5025):
  // - with discount: truthiness of the *numeric* result → string, else number 0 (100% off → 0)
  // - without discount: truthiness of *unitPrice* → string (so "0.00" stays "0.00"), else number 0
  if (discount > 0) {
    return Number(amount) ? amount : 0;
  }
  if (unitPrice === 0 || unitPrice === false || unitPrice == null || unitPrice === '') {
    return 0;
  }
  return amount;
}

// Order Item: Total Discount value for the given quantity
export function getDiscountAmtFromPercentage(discountPercentage, unitPrice, quantity, returnZero = true) {
  if(discountPercentage && discountPercentage > 0 && unitPrice && unitPrice > 0 && quantity && quantity > 0) {
    // parseInt preserves the legacy contract exactly: this function has always TRUNCATED the quantity
    // (BE quantity is an Integer). Keeping it means the only change here is float error -> exact decimal.
    return getDiscountAmtExact(discountPercentage, unitPrice, parseInt(quantity, 10) || 0);
  }
  return returnZero === true ? 0 : null;
}

// Order Item: Total Amount for the given quantity including discount and vas price
export function calculateItemTotalAmount(price, quantity, discountPercentage = 0, textPrice = 0, imagePrice = 0) {
  const lineTotal = calculateLineTotalExact(price, quantity, discountPercentage);
  return addMoneyExact(lineTotal, textPrice, imagePrice);
}

// Order Item: Total Amount for the given quantity including discount and vas price
// Have to remove calculateItemTotalAmount(), once all Vas functionality is completed
export function calculateOrderItemTotalAmount(price, quantity, discountPercentage = 0) {
  return calculateLineTotalExact(price, quantity, discountPercentage);
}

// Order Item: Calcualate Margin
export const calculateMargin = (weight, fob, tariff, shipping, unitPrice) => {
  const marginVal = calculateMarginExact(weight, fob, tariff, shipping, unitPrice);
  if (Number(marginVal)) return marginVal;
  // Same legacy gate as calculateUnitPrice: tiny nonzero pre-round → string "0.00", else number 0
  const w = Number(weight);
  const f = Number(fob);
  const t = Number(tariff);
  const s = Number(shipping);
  const u = Number(unitPrice);
  if (![w, f, t, s, u].every(Number.isFinite) || !u) return 0;
  const cost = f + w * s + f * (t / 100);
  const margin = (100 * (u - cost)) / u;
  return margin && Number(margin) ? marginVal : 0;
}

/**
 * Calculate square feet from length, width, and height (all given as strings).
 * The logic picks the two largest dimensions, multiplies them,
 * and converts square inches → square feet by dividing by 144.
 * Result is rounded to 2 decimal places.
 */
export function calculateSquareFeet(length, width, height) {
  try {
    // Convert string inputs to numbers
    const l = parseFloat(length);
    const w = parseFloat(width);
    const h = parseFloat(height);

    // If any input is not a valid number, return 0
    if (!length || !width || !height || Number.isNaN(l) || Number.isNaN(w) || Number.isNaN(h)) {
      return 0;
    }

    // Sort values to find the top 2 largest dimensions
    const values = [l, w, h].sort((a, b) => a - b);
    const top1 = values[2]; // largest
    const top2 = values[1]; // second largest

    // Calculate square feet (divide by 144 to convert sq inches → sq ft)
    const squareFeet = (top1 * top2) / 144;

    // Round to 2 decimal places and return as a number
    return parseFloat(squareFeet.toFixed(2));
  } catch (e) {
    // In case of unexpected errors, return 0
    return 0;
  }
}

/**
 * Calculates the total quantity of items available.
 */
export function calculateProductTotalQuantity(unitsInStock, committed, shortFall) {
  const stock = Number(unitsInStock) || 0;
  const commit = Number(committed) || 0;
  const fall = Number(shortFall) || 0;

  // Compute total: stock + committed - shortfall
  // Clamp result to 0 if negative
  return Math.max(stock + commit - fall, 0);
}


export const displaySurcharge = (user) => {
  return (
    user &&
    user.is_sur_charge_enabled === true &&
    (isAdminOrStaff(user) || isCustomer(user))
  );
};

/**
* Validate the input value for percentage fields.
* Accept only from 0 to 100
*
* @param inputValue // Entered input value
*/
export const validatePercentageValue = (inputValue) => {
  let value;
  if (Number(inputValue)) {
    if (inputValue < 0) {
      value = 0;
    } else if (inputValue > 100) {
      value = 100;
    } else {
      value = inputValue;
    }
    return parseFloat(value);
  }
  return inputValue;
};


/**
* Check whether the given media is pdf file
*/
export  function isPDFFile(mediaP) {
  if (!mediaP) {
    return false;
  }
  return _.startsWith(mediaP.url, 'data:application/pdf') || mediaP?.mime === 'application/pdf' || mediaP['Content-Type'] === 'application/pdf';
}

export  function isHeicFile(mediaP) {
  if (!mediaP) {
    return false;
  }
  return _.startsWith(mediaP.url, 'data:image/heic') || mediaP?.mime === 'image/heic' || mediaP['Content-Type'] === 'image/heic';
}

/**
 * Check whether the given media is image file like jpeg, jpg, png
*/
export function isImageFile(mediaP) {
  if (!mediaP) {
    return false;
  }
  // Signed links leave `data` empty (PGSS-5505); type may be Content-Type, content_type, or mime.
  if (typeof mediaP.url === 'string' && _.startsWith(mediaP.url, 'data:image/')) {
    return true;
  }
  const type = contentTypeOf(mediaP);
  return Boolean(type && _.startsWith(type, 'image/'));
}

/**
* Get the file src to display the cover image of added files
*/
export function getFileImageSrc(mediaP) {
  if (isImageFile(mediaP) && !isHeicFile(mediaP)){
    // PGSS-5476: resolved in one place now, because the viewers need the same answer and used to
    // work it out differently -- which is how they broke when the checklist started sending links
    // instead of bytes.
    return resolveFileSrc(mediaP) || 'assets/icons/file-download.png'
  }
  if (isPDFFile(mediaP)) {
    return "assets/icons/pdf.png"
  }
  return 'assets/icons/file-download.png'
}

/**
* Handles File download
*/
export const handleDownload = (media) => {
  const link = document.createElement("a");
  // PGSS-5535: `download` is honoured only for same-origin, blob: and data: URLs. Since documents
  // became presigned links to object storage (PGSS-5477 / PGSS-5505) this href is cross-origin, so
  // the browser silently ignores `download` and treats the click as ordinary navigation -- the file
  // renders over the page the user was working on. Nothing changed in this function; the data under
  // it did, which is why it regressed without anyone touching it.
  //
  // Opening a new tab is the smallest fix that stops the workflow being destroyed. The proper fix is
  // to presign with response-content-disposition=attachment so it downloads rather than renders;
  // that is server-side and tracked separately.
  //
  // PGSS-5577: that server-side fix. The API now sends `download_url` beside `url`, signed so the
  // store answers with Content-Disposition: attachment. The browser saves on that header whatever
  // the origin, so it is followed in THIS tab: the page does not navigate, and there is no blank tab
  // flashing open and shut. `url` is left to View, which is meant to display the file.
  const downloadUrl = media?.download_url;
  const remoteUrl = media?.url;
  // Communication payloads use `mime`; Attachments often use `content_type` / Content-Type.
  // contentTypeOf covers all of them so local/dev (no signer → inline bytes) still downloads.
  link.href = downloadUrl || remoteUrl || `data:${contentTypeOf(media)};base64,${media?.data}`;
  link.download = media?.filename || media?.name || "attachment";
  if (!downloadUrl && remoteUrl) {
    // A response without download_url (an older API, or a response type that does not carry one)
    // keeps the PGSS-5535 behaviour rather than rendering the file over the user's work.
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


/**
* Can view the given media in seperate tab
*/
export function canViewFile(media) {
   return (isPDFFile(media) || isImageFile(media))  && !isHeicFile(media)
}

/**
* On click view Image/Pdf file. Download file for other type of files
*/
export function onFileViwerClick(media) {
  if (isPDFFile(media)) {
    pdfViewerWindow(resolveFileSrc(media));
  } else if (isImageFile(media)) {
    ImageViewerWindow([media]);
  } else {
    // Trigger file download for other than pdf or image file like .ai, .plt, .dxf files
    handleDownload(media)
  }
}

/**
* Get stage URL from stage
*/
export const stageNametoURLNameMapping = {
  order: 'orders',
  quote: 'quotes',
  rfq: 'rfqs'
};


export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export const numberFormatter = new Intl.NumberFormat('en-US');

export const preventNegativeValues = (e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()

export const preventNegativeDecimalValues = (e) => ["e", "E", "+", "-","."].includes(e.key) && e.preventDefault()

/**
* Utility function to check if a given amount is a valid positive number
*/
export const isValidPositiveNumber = (amount) => {
  return amount && Number(amount) > 0;
}

/**
* Check for a user mission to do a given operation
*
* @param user // Permission will be taken from user
* @param operation // Operation needs to be checked
* @param module // In which module operation needs to be checked
* @param checkAllOpearation // To check for complete operation is allowed that module
*/
export const hasPermission = (user, operation, module = null, checkAllOpearation = true) => {
  // To check for given operation is allowed for that module
  // If module passed the full operation text will be prepared from operation and module
  const opertaionName = module ? `PRIV_${operation?.toUpperCase()}_${module?.toUpperCase()}` : operation?.toUpperCase();

  // To check for complete operation is allowed for that module
  const allOperationName =  (checkAllOpearation === true && module) ? `PRIV_${module?.toUpperCase()}_*` : null;

  return user?.permissions?.length > 0 && ((allOperationName && user?.permissions?.includes(allOperationName)) || user?.permissions?.includes(opertaionName));
}

/**
* Check for a user has any of the given permission
*
* @param user // Permission will be taken from user
* @param operations // Operations needs to be checked
*/
export const hasAnyPermissions = (user, operations) => {
  // Check if at least one permission in `operations` is in `user?.permissions`
  return user?.permissions?.length > 0 && user?.permissions?.some(permission => operations.includes(permission));
}

/**
 * Display name mappings
 */
export const displayName = {
  tax: "Tariffs & Customes Fee",
  weight: "Weight (lbs)",
  invoicePaidAmt: "Applied Amount", // Label for Paid Amount in customer invoice
};

const client = process.env.REACT_APP_CUSTOMER || 'psg'
export const getCustomerBasedProfile = config[client] || {}